import { BreadcrumbGroup } from '@cloudscape-design/components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMemo, useCallback } from 'react';
import React from 'react';
import routePaths from 'src/contstant/routePaths';
import { ROUTE_RISK_ENGINE, ROUTE_RISK_ENGINE_DETAIL } from 'src/v2/routes';

/** Trailing crumb label when the breakdown URL carries no station (defensive; the row link always does). */
const RISK_ENGINE_BREAKDOWN_FALLBACK_LABEL = 'Breakdown';

const AppBreadcrumbs = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Memoize the path segments calculation
  const pathSegments = useMemo(
    () => location.pathname.split('/').filter(Boolean),
    [location.pathname],
  );

  // Memoize the text transformation function
  const formatText = useCallback(
    (path: string) =>
      Object.values(routePaths).find((route) => route.path.includes(path))?.title || '',
    [],
  );

  // Memoize the breadcrumb items
  const breadcrumbItems = useMemo(() => {
    // Don't show breadcrumbs on the home page
    if (location.pathname === '/') {
      return [];
    }

    // Risk Engine breakdown: "Home > All stations > {station}". "All stations" is a middle crumb
    // (so it stays clickable — Cloudscape renders the last crumb as the non-navigable current page)
    // and links back to the Risk Engine list. The trailing crumb is the current station from the URL.
    if (location.pathname === ROUTE_RISK_ENGINE_DETAIL) {
      const station = new URLSearchParams(location.search ?? '').get('location');
      return [
        { text: 'Home', href: '/' },
        { text: 'All stations', href: ROUTE_RISK_ENGINE },
        {
          text: station || RISK_ENGINE_BREAKDOWN_FALLBACK_LABEL,
          href: ROUTE_RISK_ENGINE_DETAIL,
        },
      ];
    }

    const items = [{ text: 'Home', href: '/' }];

    pathSegments.forEach((segment, index) => {
      const href = `/${pathSegments.slice(0, index + 1).join('/')}`;
      items.push({
        text: formatText(segment),
        href,
      });
    });

    return items;
  }, [pathSegments, formatText, location.pathname, location.search]);

  return (
    <BreadcrumbGroup
      data-testid="breadcrum-group"
      onFollow={(event) => {
        if (!event.detail.external) {
          event.preventDefault();
          navigate(event.detail.href);
        }
      }}
      items={breadcrumbItems}
    />
  );
};

export default React.memo(AppBreadcrumbs);
